import * as THREE from 'three';

/** Blend rigid bone transforms without the radial shrink of linear matrix skinning. */
export const SKIN_VOLUME = 'skinVolume';

const helpers = /* glsl */`
#ifdef USE_SKINNING
attribute float skinVolume;
uniform float volumeSkinEnabled;
vec4 volumeMultiply(vec4 a, vec4 b) {
  return vec4(a.w*b.xyz+b.w*a.xyz+cross(a.xyz,b.xyz), a.w*b.w-dot(a.xyz,b.xyz));
}
vec3 volumeRotate(vec4 q, vec3 p) { return p+2.0*cross(q.xyz,cross(q.xyz,p)+q.w*p); }
vec4 volumeQuaternion(mat3 m) {
  float trace=m[0][0]+m[1][1]+m[2][2];
  vec4 q;
  if(trace>0.0) {
    float s=0.5/sqrt(trace+1.0);
    q=vec4((m[1][2]-m[2][1])*s,(m[2][0]-m[0][2])*s,(m[0][1]-m[1][0])*s,0.25/s);
  } else if(m[0][0]>m[1][1] && m[0][0]>m[2][2]) {
    float s=2.0*sqrt(1.0+m[0][0]-m[1][1]-m[2][2]);
    q=vec4(0.25*s,(m[1][0]+m[0][1])/s,(m[2][0]+m[0][2])/s,(m[1][2]-m[2][1])/s);
  } else if(m[1][1]>m[2][2]) {
    float s=2.0*sqrt(1.0+m[1][1]-m[0][0]-m[2][2]);
    q=vec4((m[1][0]+m[0][1])/s,0.25*s,(m[2][1]+m[1][2])/s,(m[2][0]-m[0][2])/s);
  } else {
    float s=2.0*sqrt(1.0+m[2][2]-m[0][0]-m[1][1]);
    q=vec4((m[2][0]+m[0][2])/s,(m[2][1]+m[1][2])/s,0.25*s,(m[0][1]-m[1][0])/s);
  }
  return normalize(q);
}
void volumeAccumulate(mat4 m, float weight, vec4 reference, inout vec4 real, inout vec4 dual, inout float scale) {
  float s=length(m[0].xyz);
  vec4 q=volumeQuaternion(mat3(m)/s);
  float signedWeight=weight*(dot(q,reference)<0.0 ? -1.0 : 1.0);
  real+=q*signedWeight;
  dual+=0.5*volumeMultiply(vec4(m[3].xyz,0.0),q)*signedWeight;
  scale+=s*weight;
}
#endif
`;

/** One switch for every figure (profiling turns it off), so materials stay free to be shared between figures. */
const enabled = { value: 1 };
function applyVolumeShader(material: THREE.Material) {
  if (material.userData.nimblezVolumeSkin) return;
  material.userData.nimblezVolumeSkin = true;
  const compile = material.onBeforeCompile, cacheKey = material.customProgramCacheKey.bind(material);
  material.userData.nimblezVolumeSkinEnabled = enabled;
  material.onBeforeCompile = (shader, renderer) => {
    compile.call(material, shader, renderer);
    shader.uniforms.volumeSkinEnabled = enabled;
    shader.vertexShader = shader.vertexShader
      .replace('#include <skinning_pars_vertex>', '#include <skinning_pars_vertex>\n' + helpers)
      .replace('#include <skinbase_vertex>', /* glsl */`
        #include <skinbase_vertex>
        #ifdef USE_SKINNING
        vec4 volumeReal=vec4(0.0), volumeDual=vec4(0.0);
        float volumeScale=0.0;
        if(skinVolume>0.0 && volumeSkinEnabled>0.5) {
          vec4 reference=volumeQuaternion(mat3(boneMatX)/length(boneMatX[0].xyz));
          volumeAccumulate(boneMatX,skinWeight.x,reference,volumeReal,volumeDual,volumeScale);
          volumeAccumulate(boneMatY,skinWeight.y,reference,volumeReal,volumeDual,volumeScale);
          volumeAccumulate(boneMatZ,skinWeight.z,reference,volumeReal,volumeDual,volumeScale);
          volumeAccumulate(boneMatW,skinWeight.w,reference,volumeReal,volumeDual,volumeScale);
          float magnitude=length(volumeReal);
          volumeReal/=magnitude; volumeDual/=magnitude;
        }
        #endif
      `)
      .replace('#include <skinnormal_vertex>', /* glsl */`
        #ifdef USE_SKINNING
        vec3 volumeNormal=objectNormal;
        #ifdef USE_TANGENT
          vec3 volumeTangent=objectTangent;
        #endif
        #include <skinnormal_vertex>
        if(skinVolume>0.0 && volumeSkinEnabled>0.5) {
          vec3 rotated=volumeRotate(volumeReal,(bindMatrix*vec4(volumeNormal,0.0)).xyz);
          objectNormal=mix(objectNormal,(bindMatrixInverse*vec4(rotated*volumeScale,0.0)).xyz,skinVolume);
          #ifdef USE_TANGENT
            rotated=volumeRotate(volumeReal,(bindMatrix*vec4(volumeTangent,0.0)).xyz);
            objectTangent=mix(objectTangent,(bindMatrixInverse*vec4(rotated*volumeScale,0.0)).xyz,skinVolume);
          #endif
        }
        #endif
      `)
      .replace('#include <skinning_vertex>', /* glsl */`
        #ifdef USE_SKINNING
        vec3 volumePosition=transformed;
        #include <skinning_vertex>
        if(skinVolume>0.0 && volumeSkinEnabled>0.5) {
          vec3 p=(bindMatrix*vec4(volumePosition,1.0)).xyz;
          vec3 translation=2.0*volumeMultiply(volumeDual,vec4(-volumeReal.xyz,volumeReal.w)).xyz;
          p=volumeRotate(volumeReal,p*volumeScale)+translation;
          transformed=mix(transformed,(bindMatrixInverse*vec4(p,1.0)).xyz,skinVolume);
        }
        #endif
      `);
  };
  material.customProgramCacheKey = () => cacheKey() + ':volume-skin-2';
  material.needsUpdate = true;
}

interface FrozenBone { matrix: THREE.Matrix4; translation: THREE.Vector3; rotation: THREE.Quaternion; scale: THREE.Vector3; matrixAt: number; decomposedAt: number }
const frozenBones = new WeakMap<THREE.Skeleton, FrozenBone[]>();
let frozen = 0, batches = 0;
/** A bone's skinning matrix, made once per frozen batch. */
function boneMatrix(skeleton: THREE.Skeleton, bone: number) {
  let list = frozenBones.get(skeleton);
  if (!list) { list = []; frozenBones.set(skeleton, list); }
  const entry = list[bone] ??= { matrix: new THREE.Matrix4(), translation: new THREE.Vector3(), rotation: new THREE.Quaternion(), scale: new THREE.Vector3(), matrixAt: 0, decomposedAt: 0 };
  if (entry.matrixAt !== frozen) { entry.matrix.multiplyMatrices(skeleton.bones[bone].matrixWorld, skeleton.boneInverses[bone]); entry.matrixAt = frozen; }
  return entry;
}
const linearIndex = new THREE.Vector4(), linearWeight = new THREE.Vector4(), linearBase = new THREE.Vector4(), linearBone = new THREE.Vector4();
/** `SkinnedMesh.applyBoneTransform` with each bone's matrix made once per frozen batch: the same arithmetic in the same order. */
function linearBoneTransform<T extends THREE.Vector3 | THREE.Vector4>(mesh: THREE.SkinnedMesh, index: number, target: T): T {
  linearIndex.fromBufferAttribute(mesh.geometry.getAttribute('skinIndex') as THREE.BufferAttribute, index);
  linearWeight.fromBufferAttribute(mesh.geometry.getAttribute('skinWeight') as THREE.BufferAttribute, index);
  if (target instanceof THREE.Vector4) { linearBase.copy(target); target.set(0, 0, 0, 0); }
  else { linearBase.set(target.x, target.y, target.z, 1); target.set(0, 0, 0); }
  linearBase.applyMatrix4(mesh.bindMatrix);
  for (let i = 0; i < 4; i++) {
    const weight = linearWeight.getComponent(i);
    if (weight !== 0) (target as THREE.Vector4).addScaledVector(linearBone.copy(linearBase).applyMatrix4(boneMatrix(mesh.skeleton, linearIndex.getComponent(i)).matrix), weight);
  }
  if (target instanceof THREE.Vector4) target.w = linearBase.w;
  return target.applyMatrix4(mesh.bindMatrixInverse) as T;
}

/**
 * Runs a synchronous batch of CPU skinning queries on these meshes (an arrow's ray test, a corpse bake slice) with their bones frozen:
 * each bone's matrix, and the decomposition volume skinning needs, is made once rather than once per weight of every vertex. Bones
 * must not move during `run`; the results are identical.
 */
export function withFrozenBones<T>(meshes: readonly THREE.Object3D[], run: () => T): T {
  const outer = frozen, linear: THREE.SkinnedMesh[] = [];
  frozen = ++batches;
  for (const mesh of meshes) if (mesh instanceof THREE.SkinnedMesh && !(mesh instanceof VolumeSkinnedMesh)) {
    linear.push(mesh); mesh.applyBoneTransform = ((index: number, target: THREE.Vector3 | THREE.Vector4) => linearBoneTransform(mesh, index, target)) as THREE.SkinnedMesh['applyBoneTransform'];
  }
  try { return run(); }
  finally {
    frozen = outer;
    for (const mesh of linear) delete (mesh as unknown as Record<string, unknown>).applyBoneTransform;
  }
}

/** CPU queries and GPU colour/shadow passes use the same shoulder deformation. */
export class VolumeSkinnedMesh extends THREE.SkinnedMesh {
  private volumeMatrix = new THREE.Matrix4();
  private volumeTranslation = new THREE.Vector3();
  private volumeScale = new THREE.Vector3();
  private volumeRotation = new THREE.Quaternion();
  private volumeDual = new THREE.Quaternion();
  private volumeRealSum = new THREE.Vector4();
  private volumeDualSum = new THREE.Vector4();
  private volumeReference = new THREE.Quaternion();
  private volumePoint = new THREE.Vector3();
  private volumeLinear = new THREE.Vector4();
  private volumeScratch = new THREE.Vector4();

  constructor(geometry = new THREE.BufferGeometry(), material: THREE.Material = new THREE.MeshBasicMaterial()) {
    super(geometry, material);
    applyVolumeShader(material);
    this.customDepthMaterial = new THREE.MeshDepthMaterial({ depthPacking: THREE.RGBADepthPacking });
    this.customDistanceMaterial = new THREE.MeshDistanceMaterial();
    applyVolumeShader(this.customDepthMaterial); applyVolumeShader(this.customDistanceMaterial);
    const disposeShadows = () => {
      material.removeEventListener('dispose', disposeShadows);
      this.customDepthMaterial?.dispose(); this.customDistanceMaterial?.dispose();
    };
    material.addEventListener('dispose', disposeShadows);
  }

  override applyBoneTransform(index: number, target: THREE.Vector3): THREE.Vector3;
  override applyBoneTransform(index: number, target: THREE.Vector4): THREE.Vector4;
  override applyBoneTransform(index: number, target: THREE.Vector3 | THREE.Vector4): THREE.Vector3 | THREE.Vector4 {
    const amount = this.geometry.getAttribute(SKIN_VOLUME)?.getX(index) ?? 0;
    if (!amount) return frozen ? linearBoneTransform(this, index, target) : super.applyBoneTransform(index, target);
    const indices = this.geometry.getAttribute('skinIndex'), weights = this.geometry.getAttribute('skinWeight');
    const w = target instanceof THREE.Vector4 ? target.w : 1;
    this.volumeLinear.set(target.x, target.y, target.z, w);
    if (amount < 1) { if (frozen) linearBoneTransform(this, index, this.volumeLinear); else super.applyBoneTransform(index, this.volumeLinear); }
    this.volumePoint.set(target.x, target.y, target.z);
    const bound = this.volumeScratch.set(target.x, target.y, target.z, w).applyMatrix4(this.bindMatrix);
    this.volumePoint.set(bound.x, bound.y, bound.z);
    this.volumeRealSum.set(0, 0, 0, 0); this.volumeDualSum.set(0, 0, 0, 0);
    let scale = 0, first = true;
    for (let c = 0; c < 4; c++) {
      const weight = weights.getComponent(index, c); if (!weight) continue;
      const bone = indices.getComponent(index, c);
      if (frozen) {
        const entry = boneMatrix(this.skeleton, bone);
        if (entry.decomposedAt !== frozen) { entry.matrix.decompose(entry.translation, entry.rotation, entry.scale); entry.decomposedAt = frozen; }
        this.volumeTranslation.copy(entry.translation); this.volumeRotation.copy(entry.rotation); this.volumeScale.copy(entry.scale);
      } else {
        this.volumeMatrix.multiplyMatrices(this.skeleton.bones[bone].matrixWorld, this.skeleton.boneInverses[bone]);
        this.volumeMatrix.decompose(this.volumeTranslation, this.volumeRotation, this.volumeScale);
      }
      if (first) { this.volumeReference.copy(this.volumeRotation); first = false; }
      const signed = weight * (this.volumeRotation.dot(this.volumeReference) < 0 ? -1 : 1);
      this.volumeDual.set(this.volumeTranslation.x, this.volumeTranslation.y, this.volumeTranslation.z, 0).multiply(this.volumeRotation);
      const q = this.volumeRotation, d = this.volumeDual;
      this.volumeRealSum.addScaledVector(this.volumeScratch.set(q.x, q.y, q.z, q.w), signed);
      this.volumeDualSum.addScaledVector(this.volumeScratch.set(d.x, d.y, d.z, d.w), signed * .5);
      scale += this.volumeScale.x * weight;
    }
    const magnitude = this.volumeRealSum.length();
    this.volumeRealSum.divideScalar(magnitude); this.volumeDualSum.divideScalar(magnitude);
    const q = this.volumeRealSum, d = this.volumeDualSum;
    this.volumeRotation.set(q.x, q.y, q.z, q.w);
    this.volumePoint.multiplyScalar(scale).applyQuaternion(this.volumeRotation);
    this.volumeDual.set(d.x, d.y, d.z, d.w).multiply(this.volumeRotation.conjugate());
    this.volumePoint.addScaledVector(this.volumeTranslation.set(this.volumeDual.x, this.volumeDual.y, this.volumeDual.z), 2 * w);
    const result = this.volumeScratch.set(this.volumePoint.x, this.volumePoint.y, this.volumePoint.z, w).applyMatrix4(this.bindMatrixInverse);
    if (amount < 1) result.lerp(this.volumeLinear, 1 - amount);
    if (target instanceof THREE.Vector4) target.copy(result);
    else target.set(result.x, result.y, result.z);
    return target;
  }
}
